import { useAppDispatch, useAppSelector } from '@/shared/hooks/useStore';
import { useTrans } from '@/shared/hooks/useTranslation';
import { formatDisplayVersion } from '@/shared/utils';
import { notify } from '@/viewmodel/shared/notify';
import { useOpenAnnouncements } from '@/viewmodel/shared/versionCheck/hooks/useOpenAnnouncements';
import { copyFor } from '@/viewmodel/shared/versionCheck/mappers/releaseNotes';
import { fetchAnnouncements } from '@/viewmodel/shared/versionCheck/releaseFiles';
import {
  readOpenedVersion,
  readToastedVersion,
  writeToastedVersion,
} from '@/viewmodel/shared/versionCheck/state/announcementStorage';
import {
  announcementsReceived,
  selectCurrentAnnouncements,
} from '@/viewmodel/shared/versionCheck/state/versionCheckSlice';
import { useEffect } from 'react';

const TOAST_DURATION_MS = 8000;

export function useAnnouncements(userId: string | null): void {
  const dispatch = useAppDispatch();
  const { t, i18n } = useTrans();
  const current = useAppSelector(selectCurrentAnnouncements);
  const openCurrent = useOpenAnnouncements(userId);

  useEffect(() => {
    if (!userId) return;
    let cancelled = false;

    const load = async (): Promise<void> => {
      const archive = await fetchAnnouncements();
      if (cancelled || !archive) return;
      dispatch(
        announcementsReceived({
          releases: archive.releases,
          seenVersion: readOpenedVersion(userId),
        }),
      );
    };

    void load();
    return () => {
      cancelled = true;
    };
  }, [dispatch, userId]);

  useEffect(() => {
    if (!userId || !current) return;
    if (readToastedVersion(userId) === current.version) return;
    writeToastedVersion(userId, current.version);

    const lede = current.notes.map((n) => copyFor(n, i18n.language)).find((c) => c !== null);
    notify(
      t('whatsNew.toast', {
        version: formatDisplayVersion(current.version),
        headline: lede?.title ?? '',
      }),
      'info',
      {
        duration: TOAST_DURATION_MS,
        action: { label: t('whatsNew.toastAction'), onPress: () => openCurrent(current) },
      },
    );
  }, [current, i18n.language, openCurrent, t, userId]);
}
